import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import TrainingStreaks from "../shared/TrainingStreaks/TrainingStreaks";
import PageContent from "../shared/PageContent/PageContent";
import TasksCard from "../shared/TasksCard/TasksCard";
import TeamEventBubble from "../shared/TeamEventBubble/TeamEventBubble";
import VideoRecommendationsPanel from "../shared/VideoRecommendationsPanel/VideoRecommendationsPanel";
import "./DashboardPage.css";
import "../Organization/OrganizationPage.css";
import { Loader, Box } from "@mantine/core";
import {
  IconTarget,
  IconTrophy,
  IconHome,
  IconCheckbox,
} from "@tabler/icons-react";

const TABS = [
  { id: "home", label: "Home", icon: IconHome },
  { id: "tasks", label: "Tasks", icon: IconCheckbox },
];

const DASHBOARD_TASKS_CARD_HEIGHT = 510;
const DASHBOARD_TRAINING_PANEL_HEIGHT = DASHBOARD_TASKS_CARD_HEIGHT + 24;

const getSortTimestamp = item => {
  const raw =
    item?.updated_at ||
    item?.last_activity_at ||
    item?.started_at ||
    item?.created_at ||
    null;
  return raw ? new Date(raw).getTime() : 0;
};

const formatShortDate = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function RepDashboard() {
  const navigate = useNavigate();
  const { getVerifiedJWT, user } = useAuth();
  const [activeTab, setActiveTab] = useState("home");
  const [rankData, setRankData] = useState(null);
  const [rankLoading, setRankLoading] = useState(true);
  const [rankRatio, setRankRatio] = useState(null);
  const [inProgressCourses, setInProgressCourses] = useState([]);
  const [inProgressPrograms, setInProgressPrograms] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  const fetchRankingData = async () => {
    try {
      setRankLoading(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/training/leaderboard"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const { your_rank, total_reps, leaderboard } = result.data;

          const rank_ratio = (your_rank / total_reps) * 100;
          setRankRatio(rank_ratio);

          let minutesToMoveUp = 0;
          let nextPersonToPass = "";
          let nextRank = your_rank;
          let minutesAheadOf = 0;

          if (leaderboard && Array.isArray(leaderboard)) {
            if (your_rank > 1) {
              const yourRankIndex = your_rank - 1;
              const nextRankIndex = yourRankIndex - 1;
              if (nextRankIndex !== -1) {
                const yourEntry = leaderboard[yourRankIndex];
                const nextRankEntry = leaderboard[nextRankIndex];
                nextPersonToPass = `${nextRankEntry.first_name} ${nextRankEntry.last_name}`;
                nextRank = your_rank - 1;

                minutesToMoveUp = Math.max(
                  0,
                  nextRankEntry.minutes_watched - yourEntry.minutes_watched + 1
                );
              }
            } else if (your_rank === 1 && leaderboard.length > 1) {
              const yourEntry = leaderboard[0];
              const secondPlaceEntry = leaderboard[1];
              nextPersonToPass = `${secondPlaceEntry.first_name} ${secondPlaceEntry.last_name}`;
              nextRank = 2;

              minutesAheadOf = Math.max(
                0,
                yourEntry.minutes_watched - secondPlaceEntry.minutes_watched
              );
            }
          }

          setRankData({
            rank: your_rank,
            totalReps: total_reps,
            minutesToMoveUp,
            nextPersonToPass,
            nextRank,
            minutesAheadOf,
          });
        }
      }
    } catch (err) {
      setRankData(null);
      setRankRatio(null);
    } finally {
      setRankLoading(false);
    }
  };

  const fetchDashboardLearningData = async () => {
    try {
      setCoursesLoading(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const [coursesResponse, programsResponse] = await Promise.all([
        fetch(getApiEndpoint("/v1/training/list"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/programs/mine"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
      ]);

      if (coursesResponse.ok) {
        const details = await coursesResponse.json();
        const courses = details.data?.courses || [];
        setAllCourses(courses);

        setInProgressCourses(
          courses.filter(
            course =>
              Number(course.completed_video_count || 0) > 0 &&
              Number(course.completed_video_count || 0) <
                Number(course.total_video_count || 0)
          )
        );
      } else {
        setAllCourses([]);
        setInProgressCourses([]);
      }

      if (programsResponse.ok) {
        const details = await programsResponse.json();
        const programs = details.data || [];

        setInProgressPrograms(
          programs.filter(
            program =>
              Number(program.completed_tasks || 0) > 0 &&
              Number(program.completed_tasks || 0) <
                Number(program.total_tasks || 0)
          )
        );
      } else {
        setInProgressPrograms([]);
      }
    } catch (err) {
      setAllCourses([]);
      setInProgressCourses([]);
      setInProgressPrograms([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    fetchRankingData();
    fetchDashboardLearningData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapCourseToItem = course => ({
    id: `course-${course.id}`,
    entityType: "course",
    entityId: course.id,
    title:
      course.course_title || course.title || course.name || "Untitled Course",
    thumbnailUrl:
      course.course_thumbnail ||
      course.course_thumbnail_url ||
      course.thumbnail_url ||
      course.thumbnail ||
      null,
    duration:
      course.total_video_count != null
        ? `${course.total_video_count} video${course.total_video_count !== 1 ? "s" : ""}`
        : null,
    progress:
      Number(course.total_video_count || 0) > 0
        ? Math.round(
            (Number(course.completed_video_count || 0) /
              Number(course.total_video_count || 0)) *
              100
          )
        : null,
    progressLabel:
      Number(course.total_video_count || 0) > 0
        ? `${Number(course.completed_video_count || 0)} of ${Number(course.total_video_count || 0)} videos completed`
        : null,
    date: formatShortDate(course.updated_at || course.created_at),
    sortTimestamp: getSortTimestamp(course),
    badge: "Course",
  });

  const mapProgramToItem = program => ({
    id: `program-${program.id}`,
    entityType: "program",
    entityId: program.id,
    title:
      program.program_name ||
      program.name ||
      program.title ||
      "Untitled Program",
    thumbnailUrl:
      program.program_icon ||
      program.program_icon_url ||
      program.icon_url ||
      program.thumbnail_url ||
      null,
    duration:
      program.total_tasks != null
        ? `${program.total_tasks} task${program.total_tasks !== 1 ? "s" : ""}`
        : null,
    progress:
      Number(program.total_tasks || 0) > 0
        ? Math.round(
            (Number(program.completed_tasks || 0) /
              Number(program.total_tasks || 0)) *
              100
          )
        : null,
    progressLabel:
      Number(program.total_tasks || 0) > 0
        ? `${Number(program.completed_tasks || 0)} of ${Number(program.total_tasks || 0)} tasks completed`
        : null,
    date: formatShortDate(program.updated_at || program.created_at),
    sortTimestamp: getSortTimestamp(program),
    badge: "Program",
  });

  const toDoItems = useMemo(
    () =>
      [
        ...inProgressCourses.map(mapCourseToItem),
        ...inProgressPrograms.map(mapProgramToItem),
      ].sort((a, b) => b.sortTimestamp - a.sortTimestamp),
    [inProgressCourses, inProgressPrograms]
  );

  const newReleaseItems = useMemo(
    () =>
      allCourses
        .map(mapCourseToItem)
        .sort((a, b) => b.sortTimestamp - a.sortTimestamp),
    [allCourses]
  );

  const dashboardCustomHeader = (
    <div className="org-page-custom-header">
      <div className="org-page-header-left">
        <h1 className="org-page-main-title">Home</h1>
        <nav className="org-page-nav-tabs" role="tablist">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`org-page-nav-tab${activeTab === tab.id ? " active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  return (
    <PageContent customHeader={dashboardCustomHeader}>
      {activeTab === "home" && (
        <div className="rep-dashboard-container">
          <div className="rep-dashboard-header">
            <div className="rep-dashboard-welcome">
              <h1 className="rep-dashboard-title">
                Welcome back, {user.firstName}! 👋
              </h1>
              {rankRatio > 70 ? (
                <p className="rep-dashboard-subtitle">
                  You're on fire, Keep it up!
                </p>
              ) : rankRatio > 30 ? (
                <p className="rep-dashboard-subtitle">
                  Keep up the momentum. You're doing great!
                </p>
              ) : rankRatio ? (
                <p className="rep-dashboard-subtitle">
                  Watch some training videos today to climb the rankings.
                </p>
              ) : (
                <p className="rep-dashboard-subtitle">
                  Keep up the momentum. You're doing great!
                </p>
              )}
            </div>
          </div>

          <div className="rep-dashboard-main-grid">
            <div className="rep-dashboard-left-column">
              <div className="rep-dashboard-stats-row">
                {rankLoading ? (
                  <div className="rep-dashboard-bubble-card rep-dashboard-bubble-card-placeholder">
                    <div className="rep-dashboard-bubble-icon trophy-icon" />
                    <div className="rep-dashboard-bubble-content rep-dashboard-bubble-content-placeholder">
                      <span className="rep-dashboard-placeholder-line rep-dashboard-placeholder-line-sm" />
                      <span className="rep-dashboard-placeholder-line rep-dashboard-placeholder-line-lg" />
                    </div>
                  </div>
                ) : rankData ? (
                  <div
                    className="rep-dashboard-bubble-card clickable-card"
                    onClick={() =>
                      navigate("/performance", { state: { tab: "training" } })
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/performance", {
                          state: { tab: "training" },
                        });
                      }
                    }}
                  >
                    <div className="rep-dashboard-bubble-icon trophy-icon">
                      <IconTrophy size={20} />
                    </div>
                    <div className="rep-dashboard-bubble-content">
                      <span className="rep-dashboard-bubble-label">
                        Current Rank
                      </span>
                      <span className="rep-dashboard-bubble-value">
                        #{rankData.rank} of {rankData.totalReps}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="rep-dashboard-bubble-card">
                    <div className="rep-dashboard-bubble-icon trophy-icon">
                      <IconTrophy size={20} />
                    </div>
                    <div className="rep-dashboard-bubble-content">
                      <span className="rep-dashboard-bubble-label">
                        Current Rank
                      </span>
                      <span className="rep-dashboard-bubble-value">
                        Unavailable
                      </span>
                    </div>
                  </div>
                )}

                <TrainingStreaks />

                {rankLoading ? (
                  <div className="rep-dashboard-bubble-card move-up-card move-up-card-large rep-dashboard-bubble-card-placeholder">
                    <div className="rep-dashboard-bubble-icon move-up-icon" />
                    <div className="rep-dashboard-bubble-content-large rep-dashboard-bubble-content-placeholder">
                      <span className="rep-dashboard-placeholder-line rep-dashboard-placeholder-line-full" />
                      <span className="rep-dashboard-placeholder-line rep-dashboard-placeholder-line-md" />
                    </div>
                  </div>
                ) : rankData ? (
                  <div
                    className="rep-dashboard-bubble-card move-up-card move-up-card-large clickable-card"
                    onClick={() =>
                      navigate("/performance", { state: { tab: "training" } })
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/performance", {
                          state: { tab: "training" },
                        });
                      }
                    }}
                  >
                    <div className="rep-dashboard-bubble-icon move-up-icon">
                      <IconTarget size={20} />
                    </div>
                    <div className="rep-dashboard-bubble-content-large">
                      {rankData.rank === 1 ? (
                        <>
                          <span className="rep-dashboard-bubble-value-main">
                            You're #1 on the leaderboard! 🏆
                          </span>
                          {rankData.nextPersonToPass && (
                            <span className="rep-dashboard-bubble-subtitle">
                              {rankData.nextPersonToPass} is{" "}
                              {rankData.minutesAheadOf} minutes behind you
                            </span>
                          )}
                        </>
                      ) : rankData.minutesToMoveUp > 0 ? (
                        <>
                          <span className="rep-dashboard-bubble-value-main">
                            Watch {rankData.minutesToMoveUp} more minutes to
                            move up
                          </span>
                          <span className="rep-dashboard-bubble-subtitle">
                            Pass {rankData.nextPersonToPass} and reach rank #
                            {rankData.nextRank}
                          </span>
                        </>
                      ) : (
                        <span className="rep-dashboard-bubble-value-main">
                          Keep up the great work! 💪
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rep-dashboard-bubble-card move-up-card move-up-card-large">
                    <div className="rep-dashboard-bubble-icon move-up-icon">
                      <IconTarget size={20} />
                    </div>
                    <div className="rep-dashboard-bubble-content-large">
                      <span className="rep-dashboard-bubble-value-main">
                        Keep up the great work! 💪
                      </span>
                      <span className="rep-dashboard-bubble-subtitle">
                        Training rank insight is temporarily unavailable
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="rep-dashboard-section pick-up-section">
                <h2 className="rep-dashboard-section-title">Jump Back In</h2>
                <p className="rep-dashboard-section-subtitle">
                  Pick up where you left off or discover something new
                </p>

                {coursesLoading || rankLoading ? (
                  <Box
                    className="rep-dashboard-courses-loading"
                    style={{ height: `${DASHBOARD_TRAINING_PANEL_HEIGHT}px` }}
                  >
                    <Loader size="md" />
                  </Box>
                ) : (
                  <VideoRecommendationsPanel
                    tabs={["To Do", "New Releases"]}
                    height={DASHBOARD_TRAINING_PANEL_HEIGHT}
                    itemsByTab={{
                      "To Do": toDoItems,
                      "New Releases": newReleaseItems,
                    }}
                    onItemClick={item => {
                      if (item.entityType === "program") {
                        navigate(`/programs/${item.entityId}`);
                        return;
                      }
                      navigate("/training", {
                        state: { courseId: item.entityId },
                      });
                    }}
                  />
                )}
              </div>
            </div>

            <div className="rep-dashboard-right-sidebar">
              <div className="rep-dashboard-sidebar-section">
                <TeamEventBubble />
              </div>
              <div className="rep-dashboard-sidebar-section">
                <TasksCard height={DASHBOARD_TASKS_CARD_HEIGHT} />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "tasks" && <TasksCard height={600} />}
    </PageContent>
  );
}

export default RepDashboard;
